import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import type { SeriesSettings, SeriesSettingsBody } from '@contracts/series';
import type { SeriesWriteupRule } from '@contracts/templates';
import { useSlack, useSlackChannels, useTemplates } from '../api/queries';
import { Button, MenuButton } from '../ui';

/**
 * "After every meeting, write it up as … and post it to …" (PRD 61).
 *
 * Aleksandr at the AI Talks: a short summary of every sprint, by his own
 * prompt, in the team's channel — for the people who will never open the
 * recording. A rule is written once the meeting's ledger has been read, so it
 * carries the owners and dates the Open page shows.
 */
export function SeriesWriteups({
  s,
  save,
}: {
  s: SeriesSettings;
  save: (body: SeriesSettingsBody, text?: string) => void;
}): ReactElement {
  const templates = useTemplates();
  const slack = useSlack();
  const connected = slack.data?.connected === true;
  const channels = useSlackChannels(connected);
  const navigate = useNavigate();
  const nameOf = (id: string) =>
    templates.data?.find((t) => t.id === id)?.name ?? 'A template that was deleted';
  const put = (rules: SeriesWriteupRule[], text: string) => save({ writeups: rules }, text);
  const used = new Set(s.writeups.map((r) => r.templateId));

  return (
    // ⚠ One line per write-up, never broken in the middle: it wrapped into
    // three — the template, then its channel and ✕, then the add — which
    // Vladimir called a UI problem (2026-10-08). A long name shortens instead.
    <div className="stack" style={{ gap: 6, width: '100%' }}>
      {s.writeups.map((rule, i) => (
        <div
          key={rule.templateId}
          style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}
        >
          <span
            className="t-ui"
            title={nameOf(rule.templateId)}
            style={{
              flex: '0 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nameOf(rule.templateId)}
          </span>
          <span className="t-secondary" style={{ flex: 'none' }}>
            →
          </span>
          {/* The channel and the ✕ never shrink: the name gives way first. */}
          <span style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
            <MenuButton
              size="sm"
              label={
                rule.slackChannelName
                  ? `# ${rule.slackChannelName}`
                  : connected
                    ? 'Not posted'
                    : 'Not posted · Slack is not connected'
              }
              search
              items={
                connected
                  ? [
                      {
                        label: 'Not posted — only written',
                        current: !rule.slackChannelId,
                        onSelect: () =>
                          put(
                            s.writeups.map((r, j) =>
                              j === i ? { ...r, slackChannelId: null, slackChannelName: null } : r,
                            ),
                            'Written after every meeting, not posted.',
                          ),
                      },
                      ...(channels.data ?? []).map((c) => ({
                        id: c.id,
                        label: `${c.isPrivate ? '🔒' : '#'} ${c.name}`,
                        current: c.id === rule.slackChannelId,
                        onSelect: () =>
                          put(
                            s.writeups.map((r, j) =>
                              j === i
                                ? { ...r, slackChannelId: c.id, slackChannelName: c.name }
                                : r,
                            ),
                            `Posted to #${c.name} after every meeting.`,
                          ),
                      })),
                    ]
                  : []
              }
            />
            <Button
              size="sm"
              variant="quiet"
              aria-label={`Stop writing it up as ${nameOf(rule.templateId)}`}
              onClick={() =>
                put(
                  s.writeups.filter((_, j) => j !== i),
                  'No longer written up after every meeting.',
                )
              }
            >
              ✕
            </Button>
          </span>
        </div>
      ))}
      {s.writeups.length < 5 ? (
        <span>
          <MenuButton
            size="sm"
            variant="quiet"
            align="left"
            label={
              s.writeups.length ? '+ Add a write-up' : 'Only the default template — add a write-up'
            }
            search
            items={(templates.data ?? []).map((t) => ({
              id: t.id,
              label: t.name,
              sub: t.description,
              // A series belongs to everybody in it, so it is written up only
              // with a template everybody in it can see.
              disabledBecause: used.has(t.id)
                ? 'Already written up with this one'
                : t.kind === 'personal'
                  ? 'Only yours — share it with the workspace to use it on a series'
                  : undefined,
              onSelect: () =>
                put(
                  [
                    ...s.writeups,
                    // Posted where the series already posts, when it does.
                    {
                      templateId: t.id,
                      slackChannelId: s.slackChannelId,
                      slackChannelName: s.slackChannelName,
                    },
                  ],
                  s.slackChannelName
                    ? `Written up as “${t.name}” after every meeting, and posted to #${s.slackChannelName}.`
                    : `Written up as “${t.name}” after every meeting.`,
                ),
            }))}
            fixed={[{ label: 'Manage templates…', onSelect: () => navigate('/templates') }]}
          />
        </span>
      ) : null}
    </div>
  );
}
